import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { saveAs } from 'file-saver';

import {
  downloadAppendedSourceVideos,
  downloadJSONData,
  downloadVideoData,
  getAppendedDownloadOptions,
  getVideofeedDownloadDataByVideoFeedId,
} from '../../../../api';
import { downloadTimelineAVaultData, downloadComposerAVaultData } from '../../../../api';
import DownloadSelectedVideosModal from '../DownloadSelectedVideosModal';
import { ContainedButton, OutlinedButton } from '../../Buttons';
import { VIDEOFEED, VIDEOFEED_TYPE } from '../../../../enums';
import { useAppSelector } from '../../../../store/hooks';
import styles from './DownloadDataModal.module.scss';
import Modal from '../../Modal';
import { useMergedDownload } from './MergedDownloadProvider';
import { getDownloadErrorMessage, triggerDownload } from './helpers';

type TDownloadDataProps = {
  isOpen: boolean;
  switchModal: () => void;
  feedId: string;
};

type AppendedDownloadOptions = {
  isAppended: boolean;
  sourcesAvailable: boolean;
  estimatedDownloadHours: number;
  mergedMp4Ready: boolean;
  mergedMp4Processing: boolean;
  progress: number;
};

const canDownloadJSON = (feed: VIDEOFEED) =>
  feed.type === VIDEOFEED_TYPE.TIMELINE && (!!feed.uuid || feed.topics?.length > 0);

const canDownloadVideoDirectly = (feed: VIDEOFEED, options: AppendedDownloadOptions | null) =>
  options?.isAppended === false && feed.type !== VIDEOFEED_TYPE.MY_COMPOSER &&
  feed.videos?.length === 1 && !canDownloadJSON(feed);

const DownloadDataModal: React.FC<TDownloadDataProps> = ({ isOpen, switchModal, feedId }) => {
  const [loading, setLoading] = useState(false);
  const [showDownloadSelectedModal, setShowDownloadSelectedModal] = useState(false);
  const notification = useSnackbar();
  const startMergedDownload = useMergedDownload();
  const [thisFeed, setThisFeed] = useState<VIDEOFEED | null>(null);
  const [appendedOptions, setAppendedOptions] = useState<AppendedDownloadOptions | null>(null);
  const [showAppendedOptions, setShowAppendedOptions] = useState(false);
  const { myOrganization } = useAppSelector((state) => state.user);

  const getCurrentVideofeed = async () => {
    try {
      setLoading(true);
      setThisFeed(null);
      setAppendedOptions(null);
      setShowAppendedOptions(false);
      const res = await getVideofeedDownloadDataByVideoFeedId(feedId);
      if (res.data) {
        setThisFeed(res.data);
      }

      const optionsResponse = await getAppendedDownloadOptions(feedId);
      setAppendedOptions(optionsResponse.data);
      setShowAppendedOptions(Boolean(
        optionsResponse.data?.isAppended && res.data &&
        res.data.type !== VIDEOFEED_TYPE.MY_COMPOSER && !canDownloadJSON(res.data),
      ));
      if (res.data && canDownloadVideoDirectly(res.data, optionsResponse.data)) {
        await handleVideoDownload(res.data, optionsResponse.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    switchModal();
    setLoading(false);
    setShowAppendedOptions(false);
  };

  const switchDownloadSelecetdDataModal = () => {
    setShowDownloadSelectedModal(!showDownloadSelectedModal);
    onClose();
  };

  const handleJSONDownload = async () => {
    try {
      if (!thisFeed) return;
      setLoading(true);

      const res = await downloadJSONData(feedId);

      const jsonString = typeof res?.data === 'string' ? res.data : JSON.stringify(res.data, null, 2);

      const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });

      // Prefer server filename if provided
      const cd = res?.headers?.['content-disposition'] || '';
      const m = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const serverName = m ? decodeURIComponent(m[1].replace(/"/g, '')) : '';

      const safe = (s: string) =>
        s
          .replace(/[\\/:*?"<>|]+/g, '-')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 150);

      const date = new Date()?.toISOString()?.slice(0, 10); // YYYY-MM-DD

      let filename = serverName || `${safe(thisFeed.title)}_${date}.json`;
      if (!filename.toLowerCase().endsWith('.json')) filename += '.json';

      saveAs(blob, filename);
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        console.log(err);
      }
    } finally {
      setLoading(false);
      onClose();
    }
  };

  async function handleAvaultDownload() {
    setLoading(true);
    try {
      if (!thisFeed) return;

      const response =
        thisFeed.type === VIDEOFEED_TYPE.TIMELINE ? await downloadTimelineAVaultData(feedId) : await downloadComposerAVaultData(feedId);

      const archiveBlob = response;
      const archiveUrl = URL.createObjectURL(archiveBlob);
      const a = document.createElement('a');
      a.href = archiveUrl;
      a.setAttribute('download', `${thisFeed.original_name}.zip`);
      document.body.appendChild(a);
      a.click();
      a.remove();

      thisFeed.type !== VIDEOFEED_TYPE.MY_COMPOSER && (await handleVideoDownload());
    } catch (error) {
      const message = getDownloadErrorMessage(error);
      if (message) {
        notification.showMessage(message);
      } else {
        console.error('Failed to download archive or videos:', error);
      }
    } finally {
      setLoading(false);
    }
  }

  const onSingleDownload = () => {
    setShowDownloadSelectedModal(true);
  };

  const handleVideoDownload = async (feed = thisFeed, knownOptions = appendedOptions) => {
    if (!feed) return;
    setLoading(true);
    let shouldClose = true;

    try {
      const options = knownOptions || (await getAppendedDownloadOptions(feedId)).data;
      setAppendedOptions(options);

      if (options.isAppended) {
        shouldClose = false;
        setShowAppendedOptions(true);
        return;
      }

      await Promise.all(
        feed.videos.map(async (video, idx) => {
          const responseUrl = await downloadVideoData(video.id, true);
          triggerDownload(responseUrl.data.url, 5000 * idx);
        }),
      );
    } catch (err: any) {
      console.error(err);
      if (err?.response?.data?.message) {
        notification.showMessage(err?.response?.data?.message);
      }
    } finally {
      setLoading(false);
      if (shouldClose) {
        onClose();
      }
    }
  };

  const handleSourceVideosDownload = async () => {
    try {
      setLoading(true);
      const response = await downloadAppendedSourceVideos(feedId);
      response.data.sources.forEach(({ url }: { url: string }, index: number) => triggerDownload(url, index * 5000));
    } catch (error) {
      const message = getDownloadErrorMessage(error);
      notification.showMessage(message || 'Failed to download source videos');
    } finally {
      onClose();
    }
  };

  const handleMergedVideoDownload = () => {
    void startMergedDownload(feedId, thisFeed?.title || 'Appended video');
    onClose();
  };

  const downloadHoursLimit = Number(myOrganization?.download_hours_limit || 0);
  const downloadHoursUsed = Number(myOrganization?.download_hours_limit_used || 0);
  const estimatedDownloadHours = Number(appendedOptions?.estimatedDownloadHours || 0);
  const directVideoDownload = thisFeed && canDownloadVideoDirectly(thisFeed, appendedOptions);
  const hasMergedDownloadHours =
    appendedOptions?.mergedMp4Ready ||
    appendedOptions?.mergedMp4Processing ||
    (downloadHoursLimit > 0 && downloadHoursUsed + estimatedDownloadHours <= downloadHoursLimit);

  useEffect(() => {
    if (isOpen) {
      getCurrentVideofeed();
    }
  }, [isOpen]);

  return (
    <>
      <Modal open={isOpen} onClose={!loading ? onClose : () => {}} loading={loading}>
        <div className={styles.modalBody}>
          <div className={styles.modalTitle}>
            <div className={styles.modalTop}>
              <div className={styles.titleText}>
                {!thisFeed || (loading && !appendedOptions)
                  ? 'Loading download options...'
                  : directVideoDownload
                    ? 'Preparing download...'
                    : showAppendedOptions
                      ? 'How would you like to download this appended video?'
                      : 'Which type of data would you like to download?'}
              </div>
            </div>
          </div>
          <div className={styles.form}>
            <div className={styles.actionBtns}>
              {thisFeed && (!loading || appendedOptions) && !showAppendedOptions && !directVideoDownload && (
                <>
                  {thisFeed.type !== VIDEOFEED_TYPE.MY_COMPOSER && thisFeed?.videos?.length > 1 && !appendedOptions?.isAppended && (
                    <ContainedButton className={styles.containedButton} disabled={loading} onClick={onSingleDownload}>
                      <span>Single video</span>
                    </ContainedButton>
                  )}

                  {canDownloadJSON(thisFeed) && (
                    <ContainedButton className={styles.containedButton} disabled={loading} onClick={handleJSONDownload}>
                      <span>JSON file</span>
                    </ContainedButton>
                  )}

                  {/* {thisFeed.type === VIDEOFEED_TYPE.TIMELINE &&
                    thisFeed.videofeed_details?.id &&
                    thisFeed.videos.every((video) => video?.video_details?.id) && (
                      <ContainedButton className={styles.containedButton} disabled={loading} onClick={handleAvaultDownload}>
                        <span>{`Download AVault package`}</span>
                      </ContainedButton>
                    )} */}

                  {/* {thisFeed.type === VIDEOFEED_TYPE.COMPOSER &&
                    thisFeed.videofeed_details?.id &&
                    thisFeed.videos.every((video) => video?.video_details?.id) &&
                    thisFeed.topics?.some(
                      ({ topicDetails, clips }) => topicDetails?.id && clips?.length && clips.every((clip) => clip?.clipDetails?.id),
                    ) && (
                      <ContainedButton className={styles.containedButton} disabled={loading} onClick={handleAvaultDownload}>
                        <span>{`Download AVault package`}</span>
                      </ContainedButton>
                    )} */}

                  {thisFeed.type === VIDEOFEED_TYPE.MY_COMPOSER &&
                    thisFeed.topics?.some(
                      ({ topic_details, clips }) => topic_details?.id && clips?.length && clips.every((clip) => clip?.clip_details?.id),
                    ) &&
                    thisFeed.videos?.every((video) => video?.video_details?.id) && (
                      <ContainedButton className={styles.containedButton} disabled={loading} onClick={handleAvaultDownload}>
                        <span>{`Download AComposer package`}</span>
                      </ContainedButton>
                    )}

                  {thisFeed.type !== VIDEOFEED_TYPE.MY_COMPOSER && (
                    <ContainedButton className={styles.containedButton} disabled={loading} onClick={() => handleVideoDownload()}>
                      <span>{`Video${thisFeed.videos.length > 1 ? 's' : ''}`}</span>
                    </ContainedButton>
                  )}
                </>
              )}

              {showAppendedOptions && appendedOptions && (
                <>
                  <ContainedButton
                    className={styles.containedButton}
                    disabled={loading || !appendedOptions.sourcesAvailable}
                    onClick={handleSourceVideosDownload}
                  >
                    <span>Download source videos</span>
                  </ContainedButton>
                  <div className={styles.downloadNotice}>Downloading Source Videos does not use any Download Hours.</div>

                  {hasMergedDownloadHours ? (
                    <>
                      <ContainedButton className={styles.containedButton} disabled={loading} onClick={handleMergedVideoDownload}>
                        <span>Download merged MP4</span>
                      </ContainedButton>
                      <div className={styles.downloadNotice}>
                        {appendedOptions.mergedMp4Ready
                          ? 'A previously generated MP4 is available and will be reused. No additional download hours will be used.'
                          : appendedOptions.mergedMp4Processing
                            ? 'The merged MP4 is already being prepared. No additional job will be started.'
                            : `Downloading Merged Video will use approximately ${estimatedDownloadHours} Download Hours.`}
                      </div>
                    </>
                  ) : (
                    <div className={styles.downloadNotice}>
                      Your organisation does not have enough download hours available. Please contact Fulcrum to discuss upgrading your
                      account to download the merged video.
                    </div>
                  )}

                  {!appendedOptions.sourcesAvailable && (
                    <div className={styles.downloadNotice}>Source videos are no longer available.</div>
                  )}
                </>
              )}

              <OutlinedButton className={styles.outlinedButton} disabled={loading} kind="default" onClick={onClose}>
                <span>Cancel</span>
              </OutlinedButton>
            </div>
          </div>
        </div>
      </Modal>
      {thisFeed && (
        <DownloadSelectedVideosModal switchModal={switchDownloadSelecetdDataModal} isOpen={showDownloadSelectedModal} feed={thisFeed} />
      )}
    </>
  );
};

export default DownloadDataModal;
